import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Clock, X } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { getTrialStatus } from "@/utils/trialUtils";
import { useToast } from "@/hooks/use-toast";
import { useTrialTermination } from "@/services/trialTerminationService";

export const TrialStatusBanner = () => {
  const [status, setStatus] = useState(getTrialStatus());
  const navigate = useNavigate();
  const { toast } = useToast();
  const { terminateTrial } = useTrialTermination();

  useEffect(() => {
    const checkStatus = () => {
      const currentStatus = getTrialStatus();
      setStatus(currentStatus);

      if (currentStatus.daysRemaining <= 3 && currentStatus.daysRemaining > 0) {
        toast({
          title: "Trial Ending Soon",
          description: `Your trial will expire in ${currentStatus.daysRemaining} days. Upgrade now to keep access to all features.`,
          duration: 7000,
        });
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 1000 * 60 * 60); // Check every hour
    return () => clearInterval(interval);
  }, [toast]);

  if (!status.isActive && !status.hasExpired) {
    return null;
  }

  const handleTerminate = async () => {
    if (window.confirm('Are you sure you want to terminate your trial? This action cannot be undone.')) {
      await terminateTrial();
    }
  };

  if (status.hasExpired) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Trial Expired</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <span>Your free trial has expired. Upgrade now to continue accessing all features.</span>
          <Button 
            variant="outline" 
            onClick={() => navigate('/subscription')}
            className="ml-4 bg-white hover:bg-white/90"
          >
            Upgrade Now
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className="mb-4">
      <Clock className="h-4 w-4" />
      <AlertTitle>Trial Active</AlertTitle>
      <AlertDescription className="flex items-center justify-between">
        <span>{status.daysRemaining} days remaining in your trial</span>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleTerminate}
            className="ml-4"
          >
            <X className="w-4 h-4 mr-1" />
            End Trial
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate('/subscription')}
            className="ml-4"
          >
            Upgrade Plan
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
};